/**
 * ModelPriceModal — shared add/edit dialog for a team model price. Used by the
 * Model prices list (add) and the model detail panel (edit). Self-contained form
 * state seeded from `initial` (a price row) or empty for create.
 *
 * Prices are an open category→USD/1K map: Input and Output are fixed rows;
 * additional rows are free-text category keys. Matching is always exact model
 * name (no match-type field).
 *
 * Rendered on --surface-raised (the popover/modal token) — on --surface-base it
 * was the same colour as the page behind it in dark mode, leaving the dialog
 * with no visible edges.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { ChevronUp, ChevronDown, X } from 'lucide-react';
import { createModelPrice, updateModelPrice } from 'features/settings/api/modelPricesApi';
import { settingsSolidButtonClass, settingsIconButtonClass, settingsModalClass } from 'features/settings/components/settingsTheme';
import { activityNeutralButtonClass } from 'features/activity/styles/dataTableStyles';

const inputCls =
  'h-8 rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 text-[12px] leading-snug text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] transition-[border-color] duration-150 focus:border-[color:var(--border-strong)] focus:outline-none';

/** Stable ids for extras rows so React doesn't re-key survivors on delete. */
let extraSeq = 0;
const newExtra = () => ({ uid: `x${(extraSeq += 1)}`, key: '', value: '' });

/** Number field: leading $ + brand up/down steppers (native spinner hidden). */
function NumberField({ value, onChange, placeholder, label, step = 0.001, className = '' }) {
  const bump = (dir) => {
    const cur = value === '' || value == null ? 0 : Number(value);
    const next = Math.max(0, Math.round((cur + dir * step) * 1e8) / 1e8);
    onChange(String(next));
  };
  return (
    <div className={`relative min-w-0 ${className}`}>
      <span
        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px]"
        style={{ color: 'var(--text-muted)' }}
        aria-hidden
      >
        $
      </span>
      <input
        type="number"
        step="any"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        className={`[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${inputCls} w-full pl-6 pr-7`}
      />
      {/* 24px hit targets: the old 11px steppers were effectively unclickable. */}
      <div className="absolute right-0.5 top-1/2 flex -translate-y-1/2 flex-col">
        <button
          type="button"
          tabIndex={-1}
          aria-label={`Increase ${label}`}
          onClick={() => bump(1)}
          className="flex h-3.5 w-6 items-center justify-center hover:opacity-70"
          style={{ color: 'var(--text-muted)' }}
        >
          <ChevronUp size={12} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          tabIndex={-1}
          aria-label={`Decrease ${label}`}
          onClick={() => bump(-1)}
          className="flex h-3.5 w-6 items-center justify-center hover:opacity-70"
          style={{ color: 'var(--text-muted)' }}
        >
          <ChevronDown size={12} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

const emptyForm = () => ({ id: null, provider: '', model_pattern: '', input: '', output: '', extras: [], aliases: [] });

function rowToForm(row) {
  const prices = row.prices || {};
  const extras = Object.entries(prices)
    .filter(([k]) => k !== 'input' && k !== 'output')
    .map(([key, value]) => ({ uid: `x${(extraSeq += 1)}`, key, value: String(value) }));
  return {
    id: row.id,
    provider: row.provider || '',
    model_pattern: row.model_pattern || '',
    input: prices.input != null ? String(prices.input) : '',
    output: prices.output != null ? String(prices.output) : '',
    extras,
    aliases: Array.isArray(row.aliases) ? row.aliases : [],
  };
}

function formToPrices(form) {
  const prices = {};
  if (form.input !== '' && form.input != null) prices.input = Number(form.input);
  if (form.output !== '' && form.output != null) prices.output = Number(form.output);
  for (const { key, value } of form.extras) {
    if (key && value !== '' && value != null) prices[key.trim()] = Number(value);
  }
  return prices;
}

/**
 * Where a prefilled rate came from, when the form was seeded from the price
 * already in use rather than opened blank. Saying so matters: without it a
 * populated form looks like values the user typed and forgot.
 */
const SEED_NOTE = {
  catalog: 'Rates in use, from the model catalog. Save to pin them for your team.',
  default: "Rates in use, Trodo's defaults. Save to pin them for your team.",
};

const ModelPriceModal = ({ teamId, initial, onClose, onSaved, seededFrom }) => {
  const [form, setForm] = useState(() => (initial ? rowToForm(initial) : emptyForm()));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const titleId = useId();
  const firstFieldRef = useRef(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addExtra = () => setForm((f) => ({ ...f, extras: [...f.extras, newExtra()] }));
  const setExtra = (uid, patch) =>
    setForm((f) => ({ ...f, extras: f.extras.map((e) => (e.uid === uid ? { ...e, ...patch } : e)) }));
  const removeExtra = (uid) => setForm((f) => ({ ...f, extras: f.extras.filter((e) => e.uid !== uid) }));
  const [aliasDraft, setAliasDraft] = useState('');
  const addAlias = () => {
    const v = aliasDraft.trim();
    if (!v) return;
    setForm((f) => (f.aliases.some((a) => a.toLowerCase() === v.toLowerCase())
      ? f : { ...f, aliases: [...f.aliases, v] }));
    setAliasDraft('');
  };
  const removeAlias = (a) => setForm((f) => ({ ...f, aliases: f.aliases.filter((x) => x !== a) }));

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const validate = () => {
    if (!form.model_pattern.trim()) return 'Model name is required.';
    const prices = formToPrices(form);
    for (const [k, v] of Object.entries(prices)) {
      if (!Number.isFinite(v) || v < 0) return `Price for "${k}" must be a non-negative number.`;
    }
    if (Object.keys(prices).length === 0) return 'Set at least one price.';
    return null;
  };

  const save = async () => {
    const v = validate();
    if (v) { setFormError(v); return; }
    setSaving(true);
    setFormError(null);
    const payload = {
      provider: form.provider.trim() || null,
      model_pattern: form.model_pattern.trim(),
      match_type: 'exact',
      prices: formToPrices(form),
      aliases: form.aliases,
    };
    try {
      const res = form.id
        ? await updateModelPrice(teamId, form.id, payload)
        : await createModelPrice(teamId, payload);
      onSaved?.(res, payload, !!form.id);
    } catch (e) {
      setFormError(e?.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const labelCls = 'text-[12px]';
  const labelStyle = { color: 'var(--text-muted)' };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--surface-overlay)' }}
      onClick={() => !saving && onClose()}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={(e) => { e.preventDefault(); save(); }}
        className={`${settingsModalClass} max-h-[90vh] max-w-lg overflow-y-auto p-5 text-[color:var(--text-primary)]`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 id={titleId} className="text-[14px] font-semibold">
            {form.id ? 'Edit model price' : 'Add model price'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
            className={`${settingsIconButtonClass} -mr-1 -mt-1`}
          >
            <X size={15} strokeWidth={2} />
          </button>
        </div>

        {formError && (
          <p role="alert" className="mt-3 text-[12px] text-[color:var(--feedback-error)]">{formError}</p>
        )}

        {!form.id && SEED_NOTE[seededFrom] && (
          <p className="mt-3 text-[12px]" style={{ color: 'var(--text-muted)' }}>
            {SEED_NOTE[seededFrom]}
          </p>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className="col-span-2 block">
            <span className={labelCls} style={labelStyle}>Model name</span>
            <input
              ref={firstFieldRef}
              value={form.model_pattern}
              onChange={(e) => setField('model_pattern', e.target.value)}
              placeholder="gpt-4o, claude-sonnet-4…"
              className={`mt-1 w-full ${inputCls}`}
            />
          </label>
          <label className="col-span-2 block">
            <span className={labelCls} style={labelStyle}>Provider (optional)</span>
            <input
              value={form.provider}
              onChange={(e) => setField('provider', e.target.value)}
              placeholder="openai, anthropic…"
              className={`mt-1 w-full ${inputCls}`}
            />
          </label>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <span className="text-[12px]" style={labelStyle}>
              Prices ($ per 1K tokens)
            </span>
            <button
              type="button"
              onClick={addExtra}
              className="text-[12px] font-medium text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
            >
              Add category
            </button>
          </div>

          {/* Input / Output are fixed categories: real labels, not readOnly
              inputs styled to look like fields (which read as "disabled"). */}
          {['input', 'output'].map((k) => (
            <div key={k} className="mt-2 flex items-center gap-2">
              <span
                className="w-32 shrink-0 text-[12px] capitalize"
                style={{ color: 'var(--text-primary)' }}
                id={`price-label-${k}`}
              >
                {k}
              </span>
              <NumberField
                className="flex-1"
                label={`${k} price per 1K tokens`}
                value={form[k]}
                onChange={(v) => setField(k, v)}
                placeholder="price per 1K tokens"
              />
              <span className="w-6 shrink-0" aria-hidden />
            </div>
          ))}

          {form.extras.map((ex) => (
            <div key={ex.uid} className="mt-2 flex items-center gap-2">
              <input
                value={ex.key}
                onChange={(e) => setExtra(ex.uid, { key: e.target.value })}
                placeholder="category key"
                aria-label="Price category key"
                className={`w-32 shrink-0 ${inputCls}`}
              />
              <NumberField
                className="flex-1"
                label={`${ex.key || 'category'} price per 1K tokens`}
                value={ex.value}
                onChange={(v) => setExtra(ex.uid, { value: v })}
                placeholder="price per 1K tokens"
              />
              <button
                type="button"
                onClick={() => removeExtra(ex.uid)}
                className={settingsIconButtonClass}
                aria-label={`Remove ${ex.key || 'category'}`}
              >
                <X size={13} strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-4">
          <span className="text-[12px]" style={labelStyle}>Aliases</span>
          {form.aliases.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {form.aliases.map((a) => (
                <span
                  key={a}
                  className="inline-flex items-center gap-1 rounded-md border border-[color:var(--border-default)] px-2 py-0.5 text-[11px]"
                >
                  {a}
                  <button
                    type="button"
                    onClick={() => removeAlias(a)}
                    className="rounded hover:opacity-70"
                    style={{ color: 'var(--text-muted)' }}
                    aria-label={`Remove alias ${a}`}
                  >
                    <X size={11} strokeWidth={2.5} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="mt-2 flex items-center gap-2">
            <input
              value={aliasDraft}
              onChange={(e) => setAliasDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAlias(); } }}
              placeholder="add an alias…"
              aria-label="Add an alias"
              className={`flex-1 ${inputCls}`}
            />
            <button
              type="button"
              onClick={addAlias}
              className={activityNeutralButtonClass}
            >
              Add
            </button>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving} className={activityNeutralButtonClass}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className={settingsSolidButtonClass}>
            {saving ? 'Saving…' : form.id ? 'Save changes' : 'Add model'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ModelPriceModal;
